/**
 * @file page-background.ts
 * @description Фон отдельной страницы (phase19.16 Р2; db-design-r2 §2, вариант
 * «в»; uiux-design-r2 §3; решение пользователя 2026-10-09: умолчание — из
 * темы, страница переопределяет частично).
 *
 * Хранится в `SitePage.background` (JSON или NULL = «как у сайта»). Формат —
 * частичный: ключа нет — наследуем, `null` — «нет» (без картинки, без
 * рисунков). Подсхемы — те же, что у фона сайта в теме (`backdrop.image`,
 * `backdrop.images`), второго формата нет. Порядок разрешения: страница →
 * дизайн страницы (`SitePage.designId`, SB95б) → тема сайта.
 */

import { z } from "zod";

import { SITE_COLOR_ROLES, type SiteColorRole } from "./keys.js";
import { liftBackdropImagesV1 } from "./legacy-v1.js";
import { SITE_LIMITS } from "./limits.js";
import { cloneJson } from "./repair.js";
import {
  siteBackdropImageSchema,
  sitePageBgImageSchema,
  type SiteBackdropImage,
  type SiteHaze,
  type SitePageBgImage,
  type SiteTheme,
} from "./schema.js";

export const sitePageBackgroundSchema = z.strictObject({
  /** Роль цвета фона страницы; нет ключа — `pageBg` дизайна/темы. */
  color: z.enum(SITE_COLOR_ROLES).optional(),
  /** Картинка под страницей; null — без картинки. */
  image: sitePageBgImageSchema.nullable().optional(),
  /**
   * Рисунки фона страницы; null — без рисунков. Формат — тот же, что у темы
   * (x/y и ширина px с SB120 этапа 2); v1 (угол, S/M/L) поднимает
   * `liftPageBackgroundV1` при чтении и записи.
   */
  decor: z
    .array(siteBackdropImageSchema)
    .max(SITE_LIMITS.backdropImages, `Рисунков на фоне страницы — не больше ${SITE_LIMITS.backdropImages}`)
    .nullable()
    .optional(),
});

export type SitePageBackground = z.infer<typeof sitePageBackgroundSchema>;

/** Откуда взято значение — для подписи «как у сайта» / «свой» в кабинете. */
export type SitePageBackgroundSource = "page" | "design" | "theme";

export interface ResolvedPageBackground {
  /** Роль цвета фона. */
  colorRole: SiteColorRole;
  /** Цвет роли из дизайна страницы или темы (`#rrggbb[aa]`). */
  color: string;
  image: SitePageBgImage | null;
  decor: SiteBackdropImage[];
  /** Свечение — только у сайта (у страницы не переопределяется). */
  haze: SiteHaze;
  source: { color: SitePageBackgroundSource; image: SitePageBackgroundSource; decor: SitePageBackgroundSource };
}

/** Рисунки фона базы: включены, только если у темы/дизайна режим «рисунки». */
const baseDecor = (base: SiteTheme): SiteBackdropImage[] =>
  base.backdrop.mode === "images" ? base.backdrop.images.map((img) => ({ ...img })) : [];

/**
 * Итоговый фон страницы. `page` — `SitePage.background` (null — «как у
 * сайта»), `design` — оформление дизайна страницы (null/нет — общий вид).
 */
export function resolvePageBackground(
  theme: SiteTheme,
  page: SitePageBackground | null,
  design?: SiteTheme | null,
): ResolvedPageBackground {
  const base = design ?? theme;
  const baseSource: SitePageBackgroundSource = design ? "design" : "theme";
  const own = page ?? {};

  const colorRole = own.color ?? "pageBg";
  const image = own.image !== undefined ? own.image : base.backdrop.image;
  const decor = own.decor !== undefined ? (own.decor ?? []).map((img) => ({ ...img })) : baseDecor(base);

  return {
    colorRole,
    color: base.colors[colorRole],
    image: image ? { ...image } : null,
    decor,
    haze: { ...base.backdrop.haze },
    source: {
      color: own.color !== undefined ? "page" : baseSource,
      image: own.image !== undefined ? "page" : baseSource,
      decor: own.decor !== undefined ? "page" : baseSource,
    },
  };
}

/** Медиа фона страницы (`SitePage.mediaIds`, db-design-r2 §3): без повторов, по алфавиту. */
export function sitePageBackgroundMediaIds(page: SitePageBackground | null): string[] {
  if (!page) return [];
  const ids = new Set<string>();
  if (page.image) ids.add(page.image.mediaId);
  for (const img of page.decor ?? []) ids.add(img.mediaId);
  return [...ids].sort();
}

/**
 * Фон страницы с рисунками v1 → v2 (SB120 этап 2, db-design-sb120-stage2 §2):
 * `decor[]` с углом и S/M/L — числами (`liftBackdropImagesV1`). Вход не
 * меняется. Нужен до проверки схемы при чтении (иначе весь фон страницы
 * стал бы «как у сайта») и при записи в api (`site-pages.service`: старая
 * вкладка кабинета). `lifted` — пути для журнала моста записи.
 */
export function liftPageBackgroundV1(raw: unknown): { background: unknown; lifted: string[] } {
  const background = cloneJson(raw);
  if (typeof background !== "object" || background === null || Array.isArray(background)) {
    return { background, lifted: [] };
  }
  const record = background as Record<string, unknown>;
  if (!Array.isArray(record["decor"])) return { background, lifted: [] };
  const { images, lifted } = liftBackdropImagesV1(record["decor"], "decor");
  record["decor"] = images;
  return { background, lifted };
}

/**
 * Чтение сохранённого `SitePage.background`: NULL или битое значение — «как
 * у сайта» (null) с пометкой; витрина не падает. Рисунки v1 поднимаются.
 */
export function readPageBackground(raw: unknown): { background: SitePageBackground | null; replaced: boolean } {
  if (raw === null || raw === undefined) return { background: null, replaced: false };
  const parsed = sitePageBackgroundSchema.safeParse(liftPageBackgroundV1(raw).background);
  return parsed.success ? { background: parsed.data, replaced: false } : { background: null, replaced: true };
}
